// © 2026 KaraokeClubs. All rights reserved.

"use client";

import { useRef, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { t } from "@/lib/i18n";
import {
  getKaraokeStatus,
  getKaraokeHours,
  formatHours,
  getVenueName,
  getVenueDescription,
  getScheduleNote,
  getPriceLabel,
  DISTRICT_LABELS,
} from "@/lib/venue-helpers";
import { buildDirectionsUrl, venueDistanceKm, formatDistance } from "@/lib/geo";
import { useGeolocation } from "@/hooks/useGeolocation";
import StatusBadge, { ConfidenceDots, SourceBadges } from "@/components/StatusBadge";
import SuggestEditModal from "@/components/SuggestEditModal";
import CrowdReport from "@/components/CrowdReport";
import HoursEditor from "@/components/HoursEditor";
import type { Venue, VenueEvent, Locale, DayHours } from "@/lib/types";

const MapView = dynamic(() => import("@/components/MapView"), { ssr: false });

const TYPE_ICONS: Record<string, string> = {
  karaoke_bar: "\u{1F3A4}",
  private_room: "\u{1F3B6}",
  restaurant_karaoke: "\u{1F37D}",
  open_mic: "\u{1F3B5}",
};

interface VenueDetailClientProps {
  venue: Venue;
  locale: Locale;
  events: VenueEvent[];
}

export default function VenueDetailClient({ venue, locale, events }: VenueDetailClientProps) {
  const status = getKaraokeStatus(venue);
  const karaokeHours = getKaraokeHours(venue);
  const name = getVenueName(venue, locale);
  const description = getVenueDescription(venue, locale);
  const isOpen = status ? (status.state === "open" || status.state === "closing_soon") : false;
  const hasKnownSchedule = karaokeHours !== null;
  const typeIcon = TYPE_ICONS[venue.venue_type] || "\u{1F3A4}";

  // Geolocation — powers a turn-by-turn directions link (origin = the user) and
  // a "how far from you" label. We never block on it; links fall back to the
  // venue's own Google Maps URL when location is unavailable.
  // Gated on `mounted` so the first client paint matches the server (no badge).
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const { coords: rawCoords } = useGeolocation();
  const coords = mounted ? rawCoords : null;
  const distanceLabel = formatDistance(venueDistanceKm(venue, coords));
  // Prefer a real directions deep-link (origin + transit) when we can build one;
  // otherwise the venue's stored maps URL; otherwise nothing.
  const directionsUrl =
    venue.latitude != null && venue.longitude != null
      ? buildDirectionsUrl(venue, coords, "transit")
      : venue.google_maps_url || null;

  // Sticky header on scroll
  const heroRef = useRef<HTMLDivElement>(null);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyHeader(!entry.isIntersecting),
      { threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const displayHours = karaokeHours ?? venue.opening_hours;
  const days: { key: string; label: string; hours: DayHours | null }[] = [
    { key: "mon", label: t(locale, "mon"), hours: displayHours.mon },
    { key: "tue", label: t(locale, "tue"), hours: displayHours.tue },
    { key: "wed", label: t(locale, "wed"), hours: displayHours.wed },
    { key: "thu", label: t(locale, "thu"), hours: displayHours.thu },
    { key: "fri", label: t(locale, "fri"), hours: displayHours.fri },
    { key: "sat", label: t(locale, "sat"), hours: displayHours.sat },
    { key: "sun", label: t(locale, "sun"), hours: displayHours.sun },
  ];

  const features: { icon: string; label: string }[] = [
    venue.has_private_rooms && { icon: "\u{1F3B6}", label: t(locale, "private_rooms") },
    venue.has_food && { icon: "\u{1F37D}", label: t(locale, "food_available") },
    venue.has_drinks && { icon: "\u{1F378}", label: t(locale, "drinks_available") },
    venue.has_english_songs && { icon: "\u{1F1EC}\u{1F1E7}", label: t(locale, "english_songs") },
    venue.has_polish_songs && { icon: "\u{1F1F5}\u{1F1F1}", label: t(locale, "polish_songs") },
    venue.has_ukrainian_songs && { icon: "\u{1F1FA}\u{1F1E6}", label: t(locale, "ukrainian_songs") },
  ].filter(Boolean) as { icon: string; label: string }[];

  return (
    <div className="min-h-screen bg-zinc-950">

      {/* ── Sticky header (Airbnb-style: appears on scroll) ── */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        showStickyHeader
          ? "bg-zinc-950/90 backdrop-blur-xl border-b border-white/[0.06] translate-y-0"
          : "-translate-y-full"
      }`}>
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link href="/" className="text-zinc-400 hover:text-white transition-colors touch-manipulation p-1 -ml-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h2 className="font-bold text-sm truncate">{name}</h2>
            {status ? <StatusBadge status={status} locale={locale} /> : (
              <span className="shrink-0 inline-flex items-center rounded-full font-medium px-2.5 py-1 text-xs gap-1.5 status-closed">
                {t(locale, "schedule_unknown")}
              </span>
            )}
          </div>
          {directionsUrl && (
            <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
               className="shrink-0 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold
                          active:scale-95 transition-transform touch-manipulation">
              {t(locale, "get_directions")}
            </a>
          )}
        </div>
      </header>

      {/* ── Map hero ── */}
      <div ref={heroRef} className="relative h-[50vh] sm:h-[45vh]">
        <MapView venues={[venue]} locale={locale} height="100%" userLocation={coords} fullScreen />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/60 via-transparent to-transparent pointer-events-none h-24" />
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-zinc-950 to-transparent pointer-events-none" />

        {/* Back button */}
        <Link
          href="/"
          className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl
                     bg-zinc-900/70 backdrop-blur-xl border border-white/[0.1] text-sm text-zinc-200
                     hover:text-white active:scale-95 transition-all touch-manipulation shadow-xl shadow-black/40"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t(locale, "back")}
        </Link>

        {/* Directions on map */}
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3.5 py-2.5 rounded-2xl
                       bg-violet-600/90 backdrop-blur-xl text-sm text-white font-semibold
                       active:scale-95 transition-all touch-manipulation shadow-xl shadow-violet-900/40"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t(locale, "get_directions")}
          </a>
        )}
      </div>

      {/* ── Content — overlaps map ── */}
      <div className="relative -mt-16 z-10 pb-28 sm:pb-8">
        <div className="max-w-2xl mx-auto px-4">

          {/* ── Main card ── */}
          <div className="rounded-t-[28px] sm:rounded-3xl bg-zinc-950 sm:bg-zinc-900/95 sm:backdrop-blur-xl sm:border sm:border-white/[0.08] p-5 sm:p-7 sm:shadow-2xl sm:shadow-black/40">

            {/* Handle */}
            <div className="flex justify-center mb-4 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-zinc-700" />
            </div>

            {/* ── Name + location ── */}
            <div className="mb-4">
              <h1 className="text-[22px] sm:text-[28px] font-extrabold tracking-tight leading-tight mb-1.5">{name}</h1>
              <p className="text-[13px] sm:text-sm text-zinc-500 flex items-center gap-1.5 flex-wrap">
                <svg className="w-3.5 h-3.5 text-zinc-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{DISTRICT_LABELS[venue.district] ?? venue.district} &middot; {venue.address}</span>
                {distanceLabel && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-semibold
                                   bg-sky-500/10 text-sky-300 border border-sky-500/15">
                    {t(locale, "distance_away").replace("{dist}", distanceLabel)}
                  </span>
                )}
              </p>
            </div>

            {/* ── Horizontal highlights (Airbnb-style quick stats) ── */}
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1 mb-5 -mx-1 px-1">
              <div className="shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.06] min-w-[90px]">
                <span className="text-xl">{typeIcon}</span>
                <span className="text-[11px] text-zinc-400 font-medium text-center">{t(locale, `venue_type_${venue.venue_type}`)}</span>
              </div>
              <div className={`shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl min-w-[90px] ${
                isOpen ? "bg-emerald-500/8 border border-emerald-500/15" : "bg-white/[0.03] border border-white/[0.06]"
              }`}>
                <div className={`w-3 h-3 rounded-full ${isOpen ? "bg-emerald-400" : "bg-zinc-600"}`} />
                <span className={`text-[11px] font-semibold text-center ${isOpen ? "text-emerald-400" : "text-zinc-500"}`}>
                  {isOpen ? t(locale, "open_now") : t(locale, "closed")}
                </span>
              </div>
              {venue.price_range && (
                <div className="shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.06] min-w-[90px]">
                  <span className="text-base font-bold text-amber-400">{getPriceLabel(venue.price_range)}</span>
                  <span className="text-[11px] text-zinc-400 font-medium">Price</span>
                </div>
              )}
              {venue.song_count && (
                <div className="shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl bg-violet-500/5 border border-violet-500/10 min-w-[90px]">
                  <span className="text-base font-bold text-violet-400">{venue.song_count.toLocaleString()}</span>
                  <span className="text-[11px] text-violet-400/70 font-medium">Songs</span>
                </div>
              )}
              {venue.has_private_rooms && (
                <div className="shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/[0.06] min-w-[90px]">
                  <span className="text-xl">{"\u{1F3B6}"}</span>
                  <span className="text-[11px] text-zinc-400 font-medium text-center">{t(locale, "private_rooms")}</span>
                </div>
              )}
            </div>

            {/* ── Status badge ── */}
            <div className="mb-5">
              {status ? <StatusBadge status={status} locale={locale} size="md" /> : (
                <span className="shrink-0 inline-flex items-center rounded-full font-medium px-3 py-1.5 text-sm gap-2 status-closed">
                  {t(locale, "schedule_unknown")}
                </span>
              )}
            </div>

            {/* ── Description ── */}
            {description && (
              <>
                <p className="text-[15px] text-zinc-300 leading-relaxed mb-5">{description}</p>
                <div className="border-t border-white/[0.06] mb-5" />
              </>
            )}

            {/* ── Action buttons ── */}
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mb-6">
              {directionsUrl && (
                <a href={directionsUrl} target="_blank" rel="noopener noreferrer"
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-violet-500/10 border border-violet-500/20 active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <svg className="w-5 h-5 text-violet-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-[11px] text-violet-300 font-medium">{t(locale, "get_directions")}</span>
                </a>
              )}
              {venue.phone && (
                <a href={`tel:${venue.phone}`}
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span className="text-[11px] text-zinc-400 font-medium">{t(locale, "call")}</span>
                </a>
              )}
              {venue.website && (
                <a href={venue.website} target="_blank" rel="noopener noreferrer"
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                  </svg>
                  <span className="text-[11px] text-zinc-400 font-medium">{t(locale, "visit_website")}</span>
                </a>
              )}
              {venue.reservation_url && (
                <a href={venue.reservation_url} target="_blank" rel="noopener noreferrer"
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  <span className="text-[11px] text-zinc-400 font-medium">{t(locale, "book_now")}</span>
                </a>
              )}
              {venue.instagram && (
                <a href={venue.instagram} target="_blank" rel="noopener noreferrer"
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <span className="text-lg">IG</span>
                  <span className="text-[11px] text-zinc-400 font-medium">Instagram</span>
                </a>
              )}
              {venue.facebook && (
                <a href={venue.facebook} target="_blank" rel="noopener noreferrer"
                   className="shrink-0 flex flex-col items-center gap-1 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/[0.06] active:scale-95 transition-all touch-manipulation min-w-[72px]">
                  <span className="text-lg">FB</span>
                  <span className="text-[11px] text-zinc-400 font-medium">Facebook</span>
                </a>
              )}
            </div>

            <div className="border-t border-white/[0.06] mb-6" />

            {/* ── What this place offers ── */}
            {features.length > 0 && (
              <>
                <h2 className="text-[15px] font-bold mb-4">{t(locale, "features")}</h2>
                <div className="space-y-3 mb-6">
                  {features.map((f) => (
                    <div key={f.label} className="flex items-center gap-3">
                      <span className="text-lg w-6 text-center">{f.icon}</span>
                      <span className="text-[14px] text-zinc-300">{f.label}</span>
                    </div>
                  ))}
                  {venue.reservation_required && (
                    <div className="flex items-center gap-3">
                      <span className="text-lg w-6 text-center text-amber-400">!</span>
                      <span className="text-[14px] text-amber-300">{t(locale, "reservation_required")}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-white/[0.06] mb-6" />
              </>
            )}

            {/* ── Karaoke hours ── */}
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-[15px] font-bold">{hasKnownSchedule ? t(locale, "karaoke_hours") : t(locale, "all_hours")}</h2>
              <ConfidenceDots confidence={venue.karaoke_schedule_confidence} />
            </div>
            {getScheduleNote(venue, locale) && (
              <p className="text-[13px] text-violet-400 mb-2">{getScheduleNote(venue, locale)}</p>
            )}
            {!hasKnownSchedule && (
              <p className="text-[13px] text-zinc-500 mb-3">{t(locale, "schedule_unknown_detail")}</p>
            )}
            {hasKnownSchedule && venue.karaoke_schedule_sources && venue.karaoke_schedule_sources.length > 0 && (
              <div className="mb-3">
                <SourceBadges sources={venue.karaoke_schedule_sources} />
              </div>
            )}
            <div className="space-y-0 mb-6">
              {days.map(({ key, label, hours }) => {
                const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
                const isToday = dayNames[new Date().getDay()] === key;

                return (
                  <div
                    key={key}
                    className={`flex justify-between py-3 text-[14px] border-b border-white/[0.04] last:border-0 ${
                      isToday ? "" : ""
                    }`}
                  >
                    <span className={`font-medium ${isToday ? "text-white" : "text-zinc-400"}`}>
                      {label}
                      {isToday && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-violet-500/15 text-violet-400">
                          TODAY
                        </span>
                      )}
                    </span>
                    <span className={`tabular-nums ${hours ? (isToday ? "text-white font-semibold" : "text-zinc-300") : "text-zinc-600"}`}>
                      {formatHours(hours)}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Crowd-sourced hours editor */}
            <HoursEditor venueId={venue.id} locale={locale} />

            {/* ── Events ── */}
            {events.length > 0 && (
              <>
                <div className="border-t border-white/[0.06] mb-6" />
                <h2 className="text-[15px] font-bold mb-4">{t(locale, "upcoming_events")}</h2>
                <div className="space-y-2.5 mb-6">
                  {events.map((event) => {
                    const title =
                      locale === "pl" && event.title_pl ? event.title_pl
                      : locale === "uk" && event.title_uk ? event.title_uk
                      : event.title_en;
                    const desc =
                      locale === "pl" && event.description_pl ? event.description_pl
                      : locale === "uk" && event.description_uk ? event.description_uk
                      : event.description_en;

                    return (
                      <div key={event.id} className="flex gap-3 items-start">
                        <div className="shrink-0 w-12 h-12 rounded-xl bg-violet-500/10 border border-violet-500/15 flex flex-col items-center justify-center">
                          <span className="text-[10px] text-violet-400 font-bold uppercase leading-none">
                            {new Date(event.date + "T00:00").toLocaleDateString(locale, { month: "short" })}
                          </span>
                          <span className="text-sm text-violet-300 font-bold leading-none">
                            {new Date(event.date + "T00:00").getDate()}
                          </span>
                        </div>
                        <div className="min-w-0 pt-0.5">
                          <p className="font-semibold text-sm">{title}</p>
                          <p className="text-xs text-zinc-500">{event.start_time}{desc ? ` — ${desc}` : ""}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Footer */}
            <CrowdReport venueId={venue.id} locale={locale} />
            <button
              onClick={() => setSuggestOpen(true)}
              className="mt-2 mb-1 inline-flex items-center gap-2 text-[13px] text-zinc-400 hover:text-violet-300
                         transition-colors active:scale-95 touch-manipulation"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t(locale, "suggest_edit")}
            </button>
            {venue.last_scraped_at && (
              <p className="text-[11px] text-zinc-600 pt-4 border-t border-white/[0.04]">
                {t(locale, "data_updated").replace(
                  "{time}",
                  new Date(venue.last_scraped_at).toLocaleDateString(
                    locale === "pl" ? "pl-PL" : locale === "uk" ? "uk-UA" : "en-US",
                    { month: "short", day: "numeric" },
                  ),
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <SuggestEditModal
        venue={venue}
        locale={locale}
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
      />

      {/* ── Sticky bottom CTA ── */}
      {directionsUrl && (
        <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden">
          <div className="px-4 pb-4 pt-3" style={{ background: "linear-gradient(to top, rgba(9,9,11,1) 60%, transparent)" }}>
            <div className="flex gap-2">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl
                           bg-gradient-to-r from-violet-600 to-violet-500 text-white font-bold text-sm
                           active:scale-[0.98] transition-all touch-manipulation shadow-xl shadow-violet-900/40"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {t(locale, "take_me_there")}
              </a>
              {venue.phone && (
                <a href={`tel:${venue.phone}`}
                   className="shrink-0 flex items-center justify-center w-14 rounded-2xl bg-zinc-800 border border-white/[0.08]
                              active:scale-95 transition-all touch-manipulation">
                  <svg className="w-5 h-5 text-zinc-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
